import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {RootState, useAppDispatch, useAppSelector} from "../../store";
import TicketService from "../../api/ticketService";
import WishService from "../../api/wishService";
import {IWinnerTicket, IWish, WishStatus, WishType} from "../../store/wish/Helper";
import {removeWishFromWishlist} from "../../store/wishlist/Slice";
import {removeWish} from "../../store/wish/Slice";
import {parseSkopjeDateMs} from "../skopjeTime";

export interface WinnerEntry {
    id: string;
    code: string;
    full_name: string;
    recipient_name?: string;
    profile_picture?: string;
}

const POLL_INTERVAL = 15000; // 15 seconds in ms
const DRAW_DURATION = 180000; // 3 minutes in ms
const POLL_START_DELAY = 75000; // 1.5 minutes in ms
const TICKET_PREFETCH_WINDOW_MS = 4000;

const useWishCountdown = (wish: IWish) => {
    const [countDown, setCountDown] = useState<number | null>(0);
    const [drawStarted, setDrawStarted] = useState(false);
    const serverTime = useAppSelector((state: RootState) => state.appReducer.serverTime);
    const [ticketsArray, setTicketsArray] = useState<WinnerEntry[]>([]);
    const [winners, setWinners] = useState<IWinnerTicket[]>([]);
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const initialFetch = useRef(false)
    const ticketsPrefetched = useRef(false);
    const dispatch = useAppDispatch()
    const isVoucher = useMemo(() => wish.type === WishType.VOUCHER, [wish.type])
    const wishEndMs = useMemo(() => parseSkopjeDateMs(wish.end_time), [wish.end_time]);

    const fetchWishDetails = useCallback(() => {
        if (isVoucher) {
            dispatch(removeWishFromWishlist(wish.id))
            dispatch(removeWish(wish.id))
            return
        }
        WishService.fetchWishDetails(wish.id)
            .then(({data}: { data: IWish }) => {
                if (data.winners?.length) {
                    setWinners(data.winners);
                }
            })
            .catch(err => console.error("Error fetching wish details", err));
    }, [wish.id, isVoucher, dispatch])

    // Calculate the countdown.
    const calculateCountdown = useCallback(() => {
        if (Number.isFinite(wishEndMs) && serverTime !== null) {
            const elapsed = serverTime - wishEndMs;

            if (elapsed < 0) {
                // Before the draw: show time until start.
                setCountDown(-elapsed);
            } else if (elapsed < DRAW_DURATION) {
                // Draw in progress: mark as started and show remaining time.
                setDrawStarted(true);
                setCountDown(DRAW_DURATION - elapsed);
            } else {
                // After 3 minutes: clear countdown.
                setCountDown(null)
                setTicketsArray((prev) => (prev.length ? [] : prev))
                setDrawStarted(false)
            }
        }
    }, [serverTime, wishEndMs]);

    useEffect(() => {
        calculateCountdown();
    }, [calculateCountdown]);

    useEffect(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }

        initialFetch.current = false;
        ticketsPrefetched.current = false;
        setCountDown(0);
        setDrawStarted(false);
        setTicketsArray([]);
        setWinners([]);
    }, [wish.id]);

    const fetchDrawTickets = useCallback(() => {
        if (isVoucher) return;
        TicketService.fetchTicketsForWish(wish.id)
            .then(res => {
                setTicketsArray(res.data);
            })
            .catch(err => console.error("Error fetching tickets", err));
    }, [wish.id, isVoucher]);

    // Warm up tickets shortly before countdown finishes so winner picker starts immediately.
    useEffect(() => {
        if (isVoucher || drawStarted || ticketsPrefetched.current) return;
        if (countDown !== null && countDown > 0 && countDown <= TICKET_PREFETCH_WINDOW_MS) {
            ticketsPrefetched.current = true;
            fetchDrawTickets();
        }
    }, [countDown, drawStarted, isVoucher, fetchDrawTickets]);

    // Fetch tickets once the draw has started.
    useEffect(() => {
        if (drawStarted) {
            fetchDrawTickets();
        }
    }, [drawStarted, fetchDrawTickets]);

    // Start polling when the draw is in progress and after the POLL_START_DELAY has passed.
    useEffect(() => {
        if (winners.length || wish.status !== WishStatus.ACTIVE) return;

        if (isVoucher && drawStarted && !initialFetch.current) {
            fetchWishDetails();
            initialFetch.current = true;
        }

        if (!initialFetch.current && countDown === null) {
            fetchWishDetails();
            initialFetch.current = true;
        }

        // Start polling when the draw is in progress and the countdown has reached the poll delay.
        if (drawStarted && countDown !== null && countDown <= (DRAW_DURATION - POLL_START_DELAY) && !pollIntervalRef.current) {
            // Trigger immediate fetch once when countDown is null and it hasn't been fetched yet.
            if (!initialFetch.current) {
                fetchWishDetails();
                initialFetch.current = true;
            }
            pollIntervalRef.current = setInterval(fetchWishDetails, POLL_INTERVAL);
        }
    }, [drawStarted, winners, countDown, fetchWishDetails, wish.status, isVoucher]);

    // Clear the polling interval when the draw is over or a winner is found.
    useEffect(() => {
        if ((winners.length || countDown === null) && pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, [winners, countDown]);

    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    return {countDown, drawStarted, setDrawStarted, ticketsArray, setTicketsArray, winners};
};

export default useWishCountdown;
